import React, {useEffect, useMemo, useState} from 'react';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import FormEditHistory from './FormEditHistory';
import PropTypes from 'prop-types';
import {
    Box,
    Checkbox,
    MenuItem,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import FORM_AUDIENCE_TYPE from '../../../../constants/FormAudienceType';
import RegularFormSettings from './RegularFormSettings';
import TriggerFormSettings, {sanitizeTriggerRecipientFieldIds} from './TriggerFormSettings';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import {FORM_ACCESS_LEVELS} from '../../../../constants/formAccessLevels';
import FORM_ASSIGNMENT_TYPE from '../../../../constants/FormAssignmentType';
import {isTemplateCompatible} from '../../../../utility/templateWizardRules';
import {
    parseRemindIntervalInput,
    validateAccessLevel,
    validateFieldLength,
    validateGroupFilters
} from '../../../../utility/validationUtil';
import EmailsInput from '../../../../shared-components/EmailsInput';
import SettingsSectionBase from './SettingsSection';
import RestrictionQuestions from './RestrictionQuestions';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import AutoAwesomeMotionOutlinedIcon from '@mui/icons-material/AutoAwesomeMotionOutlined';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import moment from 'moment/moment';
import FormExpirationDatePicker from '../FormExpirationDatePicker';
import useTextField from '../../../../utility/hooks/useTextField';
import CircleIcon from '@mui/icons-material/Circle';
import FORM_TYPE, {
    SETTINGS_FORM_TYPE_OPTIONS,
    getFormBehaviorFromType,
    getSettingsFormType
} from '../../../../constants/FormType';
import {
    PARENT_AUDIENCE_OPTION,
    PARENT_AUDIENCE_TYPE,
    mapParentAudienceOptionToType,
    mapParentAudienceTypeToOption,
} from '../../../../constants/ParentAudienceType';
import FormAccessSettings from '../FormAccessSettings';
import ApprovalSettings from './ApprovalSettings';
import AutoresponderSettings from './AutoresponderSettings';

import {
    LINKED_EMPLOYEE_AUDIENCE,
    LINKED_EMPLOYEE_TO_EMPLOYEE_AUDIENCE,
    shouldShowContactPriority,
} from './contactPriorityVisibility';

const PARENT_WITH_ACCOUNT_AUDIENCE =
    `${FORM_AUDIENCE_TYPE.parentsForChildCompletion}|${PARENT_AUDIENCE_OPTION.withAccount}`;
const PARENT_WITHOUT_ACCOUNT_AUDIENCE =
    `${FORM_AUDIENCE_TYPE.parentsForChildCompletion}|${PARENT_AUDIENCE_OPTION.withoutAccount}`;
const PARENT_APPLICANT_STATUS_AUDIENCE =
    `${FORM_AUDIENCE_TYPE.parentsForChildCompletion}|${PARENT_AUDIENCE_OPTION.applicantStatus}`;
const PARENT_WITH_ACCOUNT_LABEL = 'Parent with an Admicity account';
const PARENT_WITHOUT_ACCOUNT_LABEL = 'Parent without an Admicity account';
const PARENT_APPLICANT_STATUS_LABEL = 'Parent of applicant with Applied or Offered status';
const SETTINGS_SIDE_CLEARANCE = 'clamp(160px, 16vw, 240px)';

const markItemsForDeletion = (items = []) =>
    (items || []).reduce((acc, item) => {
        if (!item || item.isNew) {
            return acc;
        }

        acc.push(
            item.isDeleted
                ? item
                : {
                    ...item,
                    isDeleted: true
                }
        );
        return acc;
    }, []);

const PrivateFormSettings = (
    {
        formId,
        requestedSection,
        name,
        onNameChange,
        expirationDate,
        onExpirationDateChange,
        remindInterval,
        onRemindIntervalChange,
        categories,
        onCategoriesChange,
        formCategories,
        isFormLive,
        isFormArchived,
        strictRecipientsFiltering,
        allParentsCanSubmit,
        sendToPriority1ContactsOnly,
        onRollOnly = false,
        visibleToParents,
        shareResponseWithOtherParents,
        showResponsesInLinkedProfile,
        onShowResponsesInLinkedProfileChange,
        showResponsesToLinkedParents,
        onShowResponsesToLinkedParentsChange,
        responseEditing,
        onResponseEditingChange,
        profileMustBeComplete,
        allowMultipleSubmissions,
        audience,
        parentAudienceType,
        audienceOptions,
        settings,
        originalRecipientSettings,
        externalUsers,
        accessLevel,
        notificationEmails,
        onSettingsChange,
        onSetStrictRecipientsFiltering,
        onAudienceChange,
        onParentAudienceTypeChange,
        onAllParentsCanSubmitChange,
        onSendToPriority1ContactsOnlyChange,
        onVisibleToParentsChange,
        onShareResponseWithOtherParentsChange,
        onProfileMustBeComplete,
        onAllowMultipleSubmissionsChange,
        onAccessLevelChange,
        onNotificationEmailsChange,
        enableRecipientNotifications,
        onEnableRecipientNotificationsChange,
        isCommunicationForm,
        onCommunicationTypeChange,
        canSwitchToEmail,
        enableTrigger,
        setEnableTrigger,
        formQuestionAssigment,
        linkToStudents,
        onLinkToStudentsChange,
        linkToEmployees,
        onLinkToEmployeesChange,
        hasInitialisedRecipients,
        allLinkedEmployeesCanSubmit,
        onAllLinkedEmployeesCanSubmitChange,
        formAccessRef,
        schema,
        enableAutoresponder,
        onAutoresponderToggle,
        recipientType,
        templateName,
        template,
}) => {
    const [activeSettingsSection, setActiveSettingsSection] = useState('recipients');
    // AD-2693: the save pop-up opens the section where something is missing.
    useEffect(() => {
        if (requestedSection?.section) setActiveSettingsSection(requestedSection.section);
    }, [requestedSection]);
    const isCommunicationMode = isCommunicationForm === true;
    const getParentAudienceOptionFromValue = (value) => {
        const [, optionType] = String(value).split('|');

        return optionType || null;
    };
    const isLegacyParentAudience =
        audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion &&
        Number(parentAudienceType) === PARENT_AUDIENCE_TYPE.none;
    const selectedFormType = useMemo(() => getSettingsFormType({
        isCommunicationForm,
        allowMultipleSubmissions,
        enableTrigger,
        enableRecipientNotifications
    }), [
        isCommunicationForm,
        allowMultipleSubmissions,
        enableTrigger,
        enableRecipientNotifications
    ]);
    const getAudienceTypeFromValue = (value) => {
        if (value === LINKED_EMPLOYEE_AUDIENCE || value === LINKED_EMPLOYEE_TO_EMPLOYEE_AUDIENCE) {
            return FORM_AUDIENCE_TYPE.employees;
        }

        const [audienceId] = String(value).split('|');
        return Number(audienceId);
    };
    const getAccessLevelForAudienceValue = (value) => {
        const audienceType = getAudienceTypeFromValue(value);
        const optionType = getParentAudienceOptionFromValue(value);

        if (
            audienceType === FORM_AUDIENCE_TYPE.parentsForChildCompletion &&
            optionType == null
        ) {
            return accessLevel;
        }

        if (
            audienceType === FORM_AUDIENCE_TYPE.enquiries ||
            audienceType === FORM_AUDIENCE_TYPE.externalUsers ||
            audienceType === FORM_AUDIENCE_TYPE.previousSchool ||
            (
                audienceType === FORM_AUDIENCE_TYPE.parentsForChildCompletion &&
                (
                    optionType === PARENT_AUDIENCE_OPTION.withoutAccount ||
                    optionType === PARENT_AUDIENCE_OPTION.applicantStatus
                )
            )
        ) {
            return FORM_ACCESS_LEVELS.publicWithStudentLink;
        }

        return FORM_ACCESS_LEVELS.private;
    };
    const getAudienceOptionsForType = (formType) => {
        const includeLinkedEmployees = formType !== FORM_TYPE.emailCommunication;
        let hasAddedParentAudienceOptions = false;
        const baseOptions = audienceOptions.flatMap((option) => {
            if (option.value === FORM_AUDIENCE_TYPE.parentsForChildCompletion) {
                if (hasAddedParentAudienceOptions) {
                    return [];
                }

                hasAddedParentAudienceOptions = true;

                return [
                    ...(isLegacyParentAudience ? [option] : []),
                    {
                        value: PARENT_WITH_ACCOUNT_AUDIENCE,
                        label: PARENT_WITH_ACCOUNT_LABEL
                    },
                    {
                        value: PARENT_WITHOUT_ACCOUNT_AUDIENCE,
                        label: PARENT_WITHOUT_ACCOUNT_LABEL
                    },
                    {
                        value: PARENT_APPLICANT_STATUS_AUDIENCE,
                        label: PARENT_APPLICANT_STATUS_LABEL
                    }
                ];
            }

            if (
                option.value !== FORM_AUDIENCE_TYPE.employees ||
                !includeLinkedEmployees
            ) {
                return [option];
            }

            return [
                option,
                {
                    value: LINKED_EMPLOYEE_AUDIENCE,
                    label: 'Employee linked to student'
                },
                {
                    value: LINKED_EMPLOYEE_TO_EMPLOYEE_AUDIENCE,
                    label: 'Employee linked to employee'
                }
            ];
        });

        if (formType === FORM_TYPE.selfService) {
            return baseOptions.filter((option) => {
                const audienceType = getAudienceTypeFromValue(option.value);

                return (
                    audienceType === FORM_AUDIENCE_TYPE.parentsForChildCompletion ||
                    audienceType === FORM_AUDIENCE_TYPE.employees
                );
            });
        }

        if (formType === FORM_TYPE.triggerForm) {
            return baseOptions.filter((option) =>
                getAudienceTypeFromValue(option.value) !== FORM_AUDIENCE_TYPE.students
            );
        }

        return baseOptions;
    };
    const currentParentAudienceOption =
        audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion
            ? mapParentAudienceTypeToOption(parentAudienceType)
            : null;
    const selectedAudienceValue =
        audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion
             ? (isLegacyParentAudience
                 ? FORM_AUDIENCE_TYPE.parentsForChildCompletion
                 : `${FORM_AUDIENCE_TYPE.parentsForChildCompletion}|${currentParentAudienceOption ?? PARENT_AUDIENCE_OPTION.withAccount}`)
             : audience === FORM_AUDIENCE_TYPE.employees &&
             linkToStudents === true &&
            selectedFormType !== FORM_TYPE.emailCommunication
                 ? LINKED_EMPLOYEE_AUDIENCE
                 : audience === FORM_AUDIENCE_TYPE.employees &&
                 linkToEmployees === true &&
                 selectedFormType !== FORM_TYPE.emailCommunication
                     ? LINKED_EMPLOYEE_TO_EMPLOYEE_AUDIENCE
                     : audience;
    // AD-2689 (Maria, 14 October 2026): only the form types and audiences the form's template was made for
    // are offered, by the same rules as the Create Form box's template list. A blank form fits everything.
    const fitsTemplate = (formTypeId, audienceValue) => {
        const audienceType = getAudienceTypeFromValue(audienceValue);
        const isLinkedToStudent = audienceValue === LINKED_EMPLOYEE_AUDIENCE;
        const isLinkedToEmployee = audienceValue === LINKED_EMPLOYEE_TO_EMPLOYEE_AUDIENCE;

        return isTemplateCompatible({
            template,
            formType: formTypeId === FORM_TYPE.emailCommunication ? FORM_TYPE.email : formTypeId,
            audience: audienceType,
            parentAccountType: audienceType === FORM_AUDIENCE_TYPE.parentsForChildCompletion
                ? getParentAudienceOptionFromValue(audienceValue)
                : null,
            linkToStudents: audienceType === FORM_AUDIENCE_TYPE.externalUsers ? !!linkToStudents : isLinkedToStudent,
            linkToEmployees: audienceType === FORM_AUDIENCE_TYPE.externalUsers ? !!linkToEmployees : isLinkedToEmployee,
            assignmentType: audienceType === FORM_AUDIENCE_TYPE.externalUsers
                ? (linkToStudents ? FORM_ASSIGNMENT_TYPE.students : linkToEmployees ? FORM_ASSIGNMENT_TYPE.employees : FORM_ASSIGNMENT_TYPE.none)
                : null,
        });
    };
    const audienceSelectOptions = useMemo(
        () => getAudienceOptionsForType(selectedFormType)
            .filter(option => option.value === selectedAudienceValue || fitsTemplate(selectedFormType, option.value)),
        [audienceOptions, isLegacyParentAudience, selectedFormType, selectedAudienceValue, template, linkToStudents, linkToEmployees]
    );
    const isLiveFormTypeLocked =
        isFormLive &&
        (
            selectedFormType === FORM_TYPE.selfService ||
            selectedFormType === FORM_TYPE.triggerForm
        );
    const isCommunicationTypeLocked =
        selectedFormType === FORM_TYPE.emailCommunication &&
        (isFormLive || isFormArchived || !!linkToStudents || !!linkToEmployees);
    const shouldShowReminderSettings = selectedFormType !== FORM_TYPE.selfService;
    const hasAssignedRecipientsOrTrigger =
        settings && ([
                ...settings.groupFilters,
                ...settings?.studentFilters,
                ...settings?.applicationFilters,
                ...settings?.enquiryFilters,
                ...settings?.externalUserEmailAssignments,
                ...settings?.employeeFilters,
            ].filter(x => x.isDeleted == null || !x.isDeleted).length > 0 ||
            settings.triggerFormSettings?.selectedForm != null);
    const isAudienceSelectionLocked =
        isFormLive ||
        isFormArchived ||
        hasAssignedRecipientsOrTrigger;
    // A form type can only be changed between the private form types (regular, email communication,
    // self-service and trigger). A public form linked to profiles (an acceptance or offer form, or a form
    // for parents without an account, external recipients or enquiries) keeps its type (Maria, 14 October 2026).
    const isFormTypeSelectionLocked = hasAssignedRecipientsOrTrigger || accessLevel !== FORM_ACCESS_LEVELS.private;
    const shouldShowBothParentsOption =
        audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion &&
        !allowMultipleSubmissions &&
        !isCommunicationMode;
    const shouldShowVisibleToParentsOption =
        shouldShowBothParentsOption &&
        accessLevel === FORM_ACCESS_LEVELS.publicWithStudentLink;
    const shouldShowShareResponseWithOtherParentsOption =
        shouldShowBothParentsOption &&
        accessLevel === FORM_ACCESS_LEVELS.private &&
        !allParentsCanSubmit;
    const shouldShowLinkedProfileVisibilityOption =
        selectedFormType === FORM_TYPE.selfService &&
        (selectedAudienceValue === LINKED_EMPLOYEE_AUDIENCE ||
            selectedAudienceValue === LINKED_EMPLOYEE_TO_EMPLOYEE_AUDIENCE);
    const shouldShowLinkedParentVisibilityOption =
        shouldShowLinkedProfileVisibilityOption &&
        selectedAudienceValue === LINKED_EMPLOYEE_AUDIENCE;
    const shouldShowPriority1ContactsOption = shouldShowContactPriority({
        audience,
        formType: selectedFormType,
        audienceValue: selectedAudienceValue
    });
    const shouldShowProfileCompletionOption =
        audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion &&
        !isCommunicationMode;
    const shouldShowApprovalSettings =
        !isCommunicationMode &&
        !!accessLevel &&
        ![
            FORM_TYPE.application,
            FORM_TYPE.shareable
        ].includes(selectedFormType);
    const formTypeOptions = useMemo(() => SETTINGS_FORM_TYPE_OPTIONS.map((option) => {
        let disabled = false;

        if (isFormArchived) {
            disabled = option.id !== selectedFormType;
        } else if (isCommunicationTypeLocked || isLiveFormTypeLocked) {
            disabled = option.id !== selectedFormType;
        } else if (isFormLive) {
            disabled =
                option.id === FORM_TYPE.selfService ||
                option.id === FORM_TYPE.triggerForm ||
                option.id === FORM_TYPE.emailCommunication;
        }

        if (!disabled && option.id !== selectedFormType && !fitsTemplate(option.id, selectedAudienceValue)) {
            disabled = true;
        }

        if (!disabled && option.id === FORM_TYPE.emailCommunication) {
            disabled =
                !!linkToStudents ||
                (!isCommunicationMode && !canSwitchToEmail);
        }

        return {
            ...option,
            disabled
        };
    }), [
        canSwitchToEmail,
        isCommunicationMode,
        isCommunicationTypeLocked,
        isFormArchived,
        isFormLive,
        isLiveFormTypeLocked,
        linkToStudents,
        selectedFormType,
        selectedAudienceValue,
        template,
    ]);
    const formTypeHelperText = useMemo(() => {
        if (isFormArchived) {
            return 'This form type cannot be changed on an archived form.';
        }

        if (isCommunicationTypeLocked) {
            return 'Email communication cannot be changed while linked to students or when the form is live.';
        }

        if (isLiveFormTypeLocked) {
            return 'This form type cannot be changed while the form is live.';
        }

        if (linkToStudents) {
            return 'Email communication is unavailable for employee-to-student linked forms.';
        }

        if (!isCommunicationMode && !canSwitchToEmail) {
            return 'Email communication is only available when the form contains email-only content.';
        }

        return ' ';
    }, [
        canSwitchToEmail,
        isCommunicationMode,
        isCommunicationTypeLocked,
        isFormArchived,
        isLiveFormTypeLocked,
        linkToStudents
    ]);

    useEffect(() => {
        if (isCommunicationMode && enableTrigger) {
            setEnableTrigger(false);
        }
    }, [isCommunicationMode, enableTrigger, setEnableTrigger]);

    const handleAudienceChange = (event) => {
        if (isAudienceSelectionLocked) {
            return;
        }

        const selectedAudienceValue = event.target.value;
        const isLinkedToStudentAudience = selectedAudienceValue === LINKED_EMPLOYEE_AUDIENCE;
        const isLinkedToEmployeeAudience = selectedAudienceValue === LINKED_EMPLOYEE_TO_EMPLOYEE_AUDIENCE;
        const [audienceId] = String(selectedAudienceValue).split('|');
        const newAudienceType = (isLinkedToStudentAudience || isLinkedToEmployeeAudience)
            ? FORM_AUDIENCE_TYPE.employees
            : Number(audienceId);
        const nextAccessLevel = getAccessLevelForAudienceValue(selectedAudienceValue);
        const nextParentAudienceType =
            newAudienceType === FORM_AUDIENCE_TYPE.parentsForChildCompletion
                ? mapParentAudienceOptionToType(getParentAudienceOptionFromValue(selectedAudienceValue))
                : newAudienceType === FORM_AUDIENCE_TYPE.enquiries
                    ? PARENT_AUDIENCE_TYPE.enquiry
                    : PARENT_AUDIENCE_TYPE.none;

        if (newAudienceType === FORM_AUDIENCE_TYPE.students) {
            setEnableTrigger(false);
        }

        onAudienceChange(newAudienceType);
        onParentAudienceTypeChange?.(nextParentAudienceType);
        onAccessLevelChange(nextAccessLevel);

        if (newAudienceType === FORM_AUDIENCE_TYPE.employees) {
            onLinkToStudentsChange?.(isLinkedToStudentAudience);
            onLinkToEmployeesChange?.(isLinkedToEmployeeAudience);
        } else {
            onLinkToStudentsChange?.(false);
            onLinkToEmployeesChange?.(false);
        }

        if (enableTrigger) {
            onSettingsChange((prev) => ({
                ...prev,
                triggerFormSettings: sanitizeTriggerRecipientFieldIds(
                    newAudienceType,
                    prev.triggerFormSettings ?? {}
                ),
            }));
        }
    };

    const handleFormTypeChange = (event) => {
        const nextFormType = event.target.value;
        const nextIsCommunicationMode = nextFormType === FORM_TYPE.emailCommunication;
        const {
            allowMultipleSubmissions: nextAllowMultipleSubmissions,
            enableRecipientNotifications: nextEnableRecipientNotifications,
            enableTrigger: nextEnableTrigger
        } = getFormBehaviorFromType(
            nextIsCommunicationMode
                ? FORM_TYPE.email
                : nextFormType
        );
        const nextAudienceOptions = getAudienceOptionsForType(nextFormType);
        const isSelectedAudienceStillAllowed = nextAudienceOptions.some(
            (option) => String(option.value) === String(selectedAudienceValue)
        );
        const shouldFallbackLinkedEmployeeAudience =
            (
                selectedAudienceValue === LINKED_EMPLOYEE_AUDIENCE ||
                selectedAudienceValue === LINKED_EMPLOYEE_TO_EMPLOYEE_AUDIENCE
            ) &&
            [
                FORM_TYPE.emailCommunication,
                FORM_TYPE.selfService
            ].includes(nextFormType);
        const canFallbackToEmployeeAudience =
            shouldFallbackLinkedEmployeeAudience &&
            nextAudienceOptions.some(
                (option) => String(option.value) === String(FORM_AUDIENCE_TYPE.employees)
            );
        const shouldResetAudience =
            !isSelectedAudienceStillAllowed &&
            !canFallbackToEmployeeAudience;

        onAllowMultipleSubmissionsChange(
            nextIsCommunicationMode
                ? false
                : nextAllowMultipleSubmissions
        );

        if (!nextIsCommunicationMode) {
            onEnableRecipientNotificationsChange(nextEnableRecipientNotifications);
        }

        if (nextIsCommunicationMode !== isCommunicationMode) {
            onCommunicationTypeChange?.(nextIsCommunicationMode);
        }

        setEnableTrigger(nextEnableTrigger);

        if (canFallbackToEmployeeAudience) {
            onAudienceChange(FORM_AUDIENCE_TYPE.employees);
            onParentAudienceTypeChange?.(PARENT_AUDIENCE_TYPE.none);
            onAccessLevelChange(getAccessLevelForAudienceValue(FORM_AUDIENCE_TYPE.employees));
            onLinkToStudentsChange?.(false);
            onLinkToEmployeesChange?.(false);
        } else if (shouldResetAudience) {
            onAudienceChange(FORM_AUDIENCE_TYPE.none);
            onParentAudienceTypeChange?.(PARENT_AUDIENCE_TYPE.none);
            onAccessLevelChange(getAccessLevelForAudienceValue(FORM_AUDIENCE_TYPE.none));
            onLinkToStudentsChange?.(false);
            onLinkToEmployeesChange?.(false);
        }

        onSettingsChange(prev => {
            const shouldClearRecipients = nextEnableTrigger || shouldResetAudience;
            const shouldClearLinkedRecipients =
                nextEnableTrigger || shouldResetAudience || canFallbackToEmployeeAudience;
            const nextGroupFilters = shouldClearRecipients
                ? markItemsForDeletion(prev.groupFilters)
                : prev.groupFilters;

            return {
                ...prev,
                groupFilters: nextGroupFilters,
                studentFilters: shouldClearRecipients
                    ? markItemsForDeletion(prev.studentFilters)
                    : prev.studentFilters,
                employeeFilters: shouldClearRecipients
                    ? markItemsForDeletion(prev.employeeFilters)
                    : prev.employeeFilters,
                applicationFilters: shouldClearRecipients
                    ? markItemsForDeletion(prev.applicationFilters)
                    : prev.applicationFilters,
                enquiryFilters: shouldClearRecipients
                    ? markItemsForDeletion(prev.enquiryFilters)
                    : prev.enquiryFilters,
                externalUserEmailAssignments: shouldClearRecipients
                    ? markItemsForDeletion(prev.externalUserEmailAssignments)
                    : prev.externalUserEmailAssignments,
                linkedGroupFilters: shouldClearLinkedRecipients
                    ? markItemsForDeletion(prev.linkedGroupFilters)
                    : prev.linkedGroupFilters,
                linkedStudentFilters: shouldClearLinkedRecipients
                    ? markItemsForDeletion(prev.linkedStudentFilters)
                    : prev.linkedStudentFilters,
                linkedApplicationFilters: shouldClearLinkedRecipients
                    ? markItemsForDeletion(prev.linkedApplicationFilters)
                    : prev.linkedApplicationFilters,
                linkedEnquiryFilters: shouldClearLinkedRecipients
                    ? markItemsForDeletion(prev.linkedEnquiryFilters)
                    : prev.linkedEnquiryFilters,
                linkedStrictFiltering: shouldClearLinkedRecipients
                    ? false
                    : prev.linkedStrictFiltering,
                validation: shouldClearRecipients
                    ? {
                        ...validateGroupFilters(nextGroupFilters),
                        ...validateAccessLevel(accessLevel, nextGroupFilters)
                    }
                    : prev.validation,
                triggerFormSettings: nextEnableTrigger
                    ? prev.triggerFormSettings
                    : {
                        ...prev.triggerFormSettings,
                        selectedForm: null,
                        selectedFormSchema: null,
                        relatedComponent: null,
                        targetState: null,
                        relatedFormEmailFieldId: null,
                        relatedFormEmployeeFieldId: null,
                    }
            };
        });
    };

    const nameField = useTextField({
        initialValue: name ?? '',
        validate: (value) => validateFieldLength(value, 128),
        onChange: (event) => onNameChange(event.target.value)
    });

    // A section is greyed out in the sidebar when it cannot be changed on this form: the template always,
    // the form type and audience once recipients or a trigger are added (the audience also once the form
    // is live), and every section of an archived form. It follows the same locks as the boxes themselves.
    // AD-2689: the sections are listed down the side in the wizard's order, and one is shown at a time,
    // so a setting such as the recipients is found straight away.
    // AD-2689 (Maria, 14 October 2026): the same Yes/No questions, in the same order and words, as the Create Form box.
    const isEmailForm = !isCommunicationMode && [FORM_TYPE.email, FORM_TYPE.noEmail].includes(selectedFormType);
    const showResponseEditing = !isCommunicationMode && !!onResponseEditingChange;
    const isAboutAChild = audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion || !!linkToStudents;
    const settingsRestrictions = [
        isEmailForm && {
            key: 'emailNotificationsOff',
            question: 'Turn off email notifications for this form',
            checked: selectedFormType === FORM_TYPE.noEmail,
            disabled: isFormTypeSelectionLocked,
            onChange: (on) => handleFormTypeChange({target: {value: on ? FORM_TYPE.noEmail : FORM_TYPE.email}}),
        },
        shouldShowBothParentsOption && {
            key: 'bothParents',
            question: 'Both parents must complete their own separate copy of this form',
            checked: !!allParentsCanSubmit,
            disabled: isFormLive,
            // Walkthrough finding 13: a separate copy for each parent and Parent 1 only cannot both be on.
            onChange: (on) => {
                onAllParentsCanSubmitChange(on);
                if (on && sendToPriority1ContactsOnly) onSendToPriority1ContactsOnlyChange(false);
            },
        },
        shouldShowShareResponseWithOtherParentsOption && allParentsCanSubmit && {
            key: 'shareWithOtherParent',
            question: "Allow each parent to see the other parent's completed form?",
            checked: !!shareResponseWithOtherParents,
            onChange: (on) => onShareResponseWithOtherParentsChange(on),
        },
        shouldShowProfileCompletionOption && {
            key: 'profileComplete',
            question: "Block form submission if the child's profile information is incomplete",
            checked: !!profileMustBeComplete,
            onChange: (on) => onProfileMustBeComplete(on),
        },
        shouldShowPriority1ContactsOption && {
            key: 'parent1Only',
            question: selectedAudienceValue === LINKED_EMPLOYEE_AUDIENCE
                ? "Send the emails for the linked student's parents to Parent 1 only"
                : `Send this ${isCommunicationMode ? 'email' : 'form'} to Parent 1 only`,
            checked: !!sendToPriority1ContactsOnly,
            disabled: isFormLive,
            onChange: (on) => {
                onSendToPriority1ContactsOnlyChange(on);
                if (on && allParentsCanSubmit) onAllParentsCanSubmitChange(false);
            },
        },
        shouldShowVisibleToParentsOption && {
            key: 'availableToParents',
            question: "Make this form available in the parents' account and on the student profile",
            checked: !!visibleToParents,
            onChange: (on) => onVisibleToParentsChange(on),
        },
        shouldShowLinkedProfileVisibilityOption && {
            key: 'linkedProfile',
            question: "Show submitted responses on the linked person's profile",
            checked: !!showResponsesInLinkedProfile,
            onChange: (on) => {
                onShowResponsesInLinkedProfileChange?.(on);
                if (!on) onShowResponsesToLinkedParentsChange?.(false);
            },
        },
        shouldShowLinkedParentVisibilityOption && showResponsesInLinkedProfile && {
            key: 'linkedParents',
            question: "Let the child's parents see the submitted responses",
            checked: !!showResponsesToLinkedParents,
            onChange: (on) => onShowResponsesToLinkedParentsChange?.(on),
        },
        // AD-2695: the same three questions as the Create Form box.
        showResponseEditing && {
            key: 'allowResponseEditing',
            question: 'Allow submitted responses to be edited',
            checked: !!responseEditing?.allowResponseEditing,
            onChange: (on) => onResponseEditingChange({
                allowResponseEditing: on,
                editUpdatesProfile: on && !!responseEditing?.editUpdatesProfile,
                editResendsResponses: on && !!responseEditing?.editResendsResponses,
            }),
        },
        showResponseEditing && responseEditing?.allowResponseEditing && isAboutAChild && {
            key: 'editUpdatesProfile',
            question: "Update the child's profile with the edited answers",
            checked: !!responseEditing.editUpdatesProfile,
            onChange: (on) => onResponseEditingChange({...responseEditing, editUpdatesProfile: on}),
        },
        showResponseEditing && responseEditing?.allowResponseEditing && {
            key: 'editResendsResponses',
            question: 'Send the trigger forms and automatic responses again when a response is edited',
            checked: !!responseEditing.editResendsResponses,
            onChange: (on) => onResponseEditingChange({...responseEditing, editResendsResponses: on}),
        },
    ].filter(Boolean);

    const settingsPanes = [
        {
key: 'formType',
label: 'Form Type',
readOnly: isFormTypeSelectionLocked || isFormArchived || formTypeOptions.filter(option => !option.disabled && option.id !== FORM_TYPE.noEmail).length <= 1,
show: true,
node: (
            <>
                <SettingsSectionBase
                    header="Form Type"
                    headerIcon={<AutoAwesomeMotionOutlinedIcon fontSize="small"/>}
                    subHeader="Choose how this form should behave."
                >
                    <TextField
                        select
                        fullWidth
                        label="Form Type"
                        // AD-2689 (Maria, 13 October 2026): 'Regular form' is one type; turning email notifications
                        // off is a question under Restrictions, as it is in the Create Form box.
                        value={selectedFormType === FORM_TYPE.noEmail ? FORM_TYPE.email : selectedFormType}
                        onChange={(event) => {
                            if (event.target.value === FORM_TYPE.email && selectedFormType === FORM_TYPE.noEmail) return;
                            handleFormTypeChange(event);
                        }}
                        disabled={isFormTypeSelectionLocked}
                        helperText={formTypeHelperText}
                    >
                        {formTypeOptions.filter(option => option.id !== FORM_TYPE.noEmail).map((option) => (
                            <MenuItem
                                key={option.id}
                                value={option.id}
                                disabled={option.id === FORM_TYPE.email && selectedFormType === FORM_TYPE.noEmail ? false : option.disabled}
                                sx={{
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                }}
                            >
                                {option.id === FORM_TYPE.email ? 'Regular form' : option.description}
                            </MenuItem>
                        ))}
                    </TextField>
                </SettingsSectionBase>
            </>
        )
},
        {
key: 'audience',
label: 'Audience',
readOnly: isAudienceSelectionLocked || audienceSelectOptions.length <= 1,
show: true,
node: (
            <>
                <SettingsSectionBase
                    header={audience === FORM_AUDIENCE_TYPE.none ? 'Select audience' : 'Audience'}
                    headerIcon={<GroupsOutlinedIcon fontSize="small"/>}
                    subHeader={
                        audience === FORM_AUDIENCE_TYPE.none
                            ? (
                                selectedFormType === FORM_TYPE.emailCommunication
                                    ? 'Choose who will receive this email'
                                    : 'Choose who will receive and complete this form'
                            )
                            : null
                    }
                >
                    <TextField
                        select
                        fullWidth
                        label="Audience"
                        value={selectedAudienceValue}
                        onChange={handleAudienceChange}
                        disabled={isAudienceSelectionLocked}
                    >
                        <MenuItem disabled value={FORM_AUDIENCE_TYPE.none}>None</MenuItem>
                        {
                            audienceSelectOptions
                                .map((option) => (
                                    <MenuItem
                                        key={option.value}
                                        value={option.value}
                                    >
                                        {option.label}
                                    </MenuItem>
                                ))
                        }
                    </TextField>
                </SettingsSectionBase>
            </>
        )
},
        {
key: 'template',
label: 'Template',
readOnly: true,
show: true,
node: (
            <SettingsSectionBase header="Template" headerIcon={<DescriptionOutlinedIcon fontSize="small"/>}>
                <TextField
                    fullWidth
                    label="Template"
                    value={templateName || 'Blank form'}
                    disabled
                    data-testid="form-settings-template"
                />
            </SettingsSectionBase>
        )
},
        {
key: 'recipients',
label: 'Recipients',
readOnly: isFormArchived,
show: audience !== FORM_AUDIENCE_TYPE.none,
node: (
            <Box>
                        {
                            !enableTrigger || isCommunicationMode
                                ? <RegularFormSettings
                                    formId={formId}
                                    audience={audience}
                                    parentAudienceOption={currentParentAudienceOption}
                                    settings={settings}
                                    originalSettings={originalRecipientSettings}
                                    strictRecipientsFiltering={strictRecipientsFiltering}
                                    isFormLive={isFormLive}
                                    isFormArchived={isFormArchived}
                                    accessLevel={accessLevel}
                                    externalUsers={externalUsers}
                                    onSettingsChange={onSettingsChange}
                                    onSetStrictRecipientsFiltering={onSetStrictRecipientsFiltering}
                                    isCommunicationForm={isCommunicationForm}
                                    linkToStudents={linkToStudents}
                                    linkToEmployees={linkToEmployees}
                                    allLinkedEmployeesCanSubmit={allLinkedEmployeesCanSubmit}
                                    onAllLinkedEmployeesCanSubmitChange={onAllLinkedEmployeesCanSubmitChange}
                                    hasInitialisedRecipients={hasInitialisedRecipients}
                                    sendToPriority1ContactsOnly={sendToPriority1ContactsOnly}
                                    onRollOnly={onRollOnly}
                                />
                                : <>
                                    <TriggerFormSettings
                                        formId={formId}
                                        formQuestionAssigment={formQuestionAssigment}
                                        settings={settings}
                                        audience={audience}
                                        linkToStudents={linkToStudents}
                                        linkToEmployees={linkToEmployees}
                                        externalUsers={externalUsers}
                                        onSettingsChange={onSettingsChange}
                                    />
                                    {audience === FORM_AUDIENCE_TYPE.employees && linkToStudents === true && (
                                        <Box sx={{pt: 2}}>
                                            <RegularFormSettings
                                                formId={formId}
                                                audience={audience}
                                                settings={settings}
                                                originalSettings={originalRecipientSettings}
                                                strictRecipientsFiltering={strictRecipientsFiltering}
                                                isFormLive={isFormLive}
                                                isFormArchived={isFormArchived}
                                                accessLevel={accessLevel}
                                                externalUsers={externalUsers}
                                                onSettingsChange={onSettingsChange}
                                                onSetStrictRecipientsFiltering={onSetStrictRecipientsFiltering}
                                                linkToStudents={linkToStudents}
                                                linkToEmployees={linkToEmployees}
                                                allLinkedEmployeesCanSubmit={allLinkedEmployeesCanSubmit}
                                                onAllLinkedEmployeesCanSubmitChange={onAllLinkedEmployeesCanSubmitChange}
                                                triggerStaffOnly
                                            />
                                        </Box>
                                    )}
                                </>
                        }
                    </Box>
        )
},
        {
key: 'restrictions',
label: 'Restrictions',
readOnly: isFormArchived,
show: settingsRestrictions.length > 0,
node: (
            <SettingsSectionBase
                header="Restrictions"
                headerIcon={<SupervisorAccountIcon fontSize="small"/>}
                subHeader={isCommunicationMode
                    ? "Restrictions are the exception, not the rule. If you don't have a specific reason to add a restriction to your email, leave these on their default 'no' setting."
                    : "Form Restrictions are the exception, not the rule. If you don't have a specific reason to add a restriction to your form, leave these on their default 'no' setting."}
            >
                <RestrictionQuestions items={settingsRestrictions} disabled={isFormArchived}/>
            </SettingsSectionBase>
        )
},
        {
key: 'name',
label: 'Form Name',
readOnly: isFormArchived,
show: true,
node: (
            <>
                <SettingsSectionBase
                    header="Form Name"
                    headerIcon={<DescriptionOutlinedIcon fontSize="small"/>}
                >
                    <Stack spacing={3}>
                        <TextField
                            fullWidth
                            label="Form Name"
                            required={nameField.required}
                            disabled={isFormArchived}
                            value={nameField.value}
                            onChange={nameField.onChange}
                            error={nameField.error}
                            helperText={nameField.helperText || ' '}
                            autoComplete="off"
                        />
                    </Stack>
                </SettingsSectionBase>
            </>
        )
},
        {
key: 'category',
label: 'Category',
readOnly: isFormArchived,
show: true,
node: (
            <>
                <SettingsSectionBase
                    header="Category"
                    headerIcon={<CategoryOutlinedIcon fontSize="small"/>}
                >
                    <Stack spacing={3}>
                        {(() => {
                            const selectedCategories = categories || [];

                            const handleCategoryChange = (e) => {
                                onCategoriesChange(e.target.value);
                            };

                            return (
                                <TextField
                                    select
                                    fullWidth
                                    disabled={isFormArchived}
                                    value={selectedCategories}
                                    label="Categories"
                                    helperText=" "
                                    SelectProps={{
                                        multiple: true,
                                        renderValue: (selected) => {
                                            if (!formCategories?.length) {
                                                return <em>No categories</em>;
                                            }

                                            if (!Array.isArray(selected) || selected.length === 0) {
                                                return <em>No categories</em>;
                                            }

                                            const selectedCats = formCategories?.filter(c =>
                                                selected.includes(c.id)
                                            ) || [];

                                            return (
                                                <Stack flexDirection="row" gap={1} flexWrap="wrap">
                                                    {selectedCats.map(cat => (
                                                        <Box
                                                            key={cat.id}
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 0.8,
                                                                padding: 1,
                                                                borderRadius: '18px',
                                                                border: '1px solid #adadad',
                                                            }}
                                                        >
                                                            <CircleIcon
                                                                fontSize="small"
                                                                sx={{color: cat.color}}
                                                            />
                                                            <Typography variant="body2">
                                                                {cat.name}
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </Stack>
                                            );
                                        },
                                        MenuProps: {
                                            sx: {
                                                '& .MuiMenuItem-root': {
                                                    whiteSpace: 'unset',
                                                    wordBreak: 'break-all'
                                                }
                                            }
                                        }
                                    }}
                                    onChange={handleCategoryChange}
                                >
                                    {!formCategories?.length && (
                                        <MenuItem disabled>
                                            <em>No categories</em>
                                        </MenuItem>
                                    )}
                                    {formCategories?.map(c => (
                                        <MenuItem key={c.id} value={c.id}>
                                            <Checkbox checked={selectedCategories.includes(c.id)}/>

                                            <CircleIcon
                                                fontSize="small"
                                                sx={{
                                                    color: c.color,
                                                    mr: 1
                                                }}
                                            />

                                            {c.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            );
                        })()}
                    </Stack>
                </SettingsSectionBase>
            </>
        )
},
        {
key: 'expiry',
label: 'Expiry',
readOnly: isFormArchived,
// Walkthrough finding 32 (Maria, 15 October 2026): an email communication has no expiry.
show: audience !== FORM_AUDIENCE_TYPE.none && !!shouldShowReminderSettings && !isCommunicationMode,
node: (
            <SettingsSectionBase header="Expiry" headerIcon={<SettingsOutlinedIcon fontSize="small"/>}>
                <Stack spacing={3}>
                                {shouldShowReminderSettings && (
                                    <FormExpirationDatePicker
                                        label="Expiration Date (optional)"
                                        expirationDate={expirationDate}
                                        onExpirationDateChange={onExpirationDateChange}
                                        disabled={isFormArchived}
                                        minDateTime={
                                            settings.scheduledActivationAt
                                                ? moment(settings.scheduledActivationAt)
                                                : moment()
                                        }
                                    />
                                )}
                </Stack>
            </SettingsSectionBase>
        )
},
        {
key: 'notifications',
label: 'Notifications',
readOnly: isFormArchived,
show: audience !== FORM_AUDIENCE_TYPE.none && !isCommunicationMode,
node: (
            <SettingsSectionBase header="Notifications" headerIcon={<SettingsOutlinedIcon fontSize="small"/>}>
                <Stack spacing={3}>
                                {shouldShowReminderSettings && !isCommunicationMode && (
                                    <TextField
                                        fullWidth
                                        required
                                        label="Remind interval"
                                        variant="outlined"
                                        disabled={isFormArchived}
                                        value={remindInterval}
                                        onChange={(e) => {
                                            const nextValue = parseRemindIntervalInput(e.target.value);
                                            if (nextValue === null) {
                                                return;
                                            }
                                            onRemindIntervalChange(nextValue);
                                        }}
                                        helperText="In hours (0–1000, 0 = Disabled)"
                                        inputProps={{
                                            inputMode: 'numeric',
                                            pattern: '[0-9]*'
                                        }}
                                    />
                                )}
                                {!isCommunicationMode && (
                                    <EmailsInput
                                        label="Email address to be notified when this form receives submissions"
                                        values={notificationEmails}
                                        onChange={emails =>
                                            onNotificationEmailsChange(
                                                Array.from(new Map(emails.map(item => [item.email, item])).values())
                                            )
                                        }
                                        onBlur={email =>
                                            onNotificationEmailsChange(prev =>
                                                Array.from(new Map([
                                                    ...prev.map(item => [item.email, item]),
                                                    [email, {email}],
                                                ]).values())
                                            )
                                        }
                                        disabled={isFormArchived}
                                    />
                                )}
                </Stack>
            </SettingsSectionBase>
        )
},
        {
key: 'access',
label: 'Access',
readOnly: isFormArchived,
show: true,
node: (
            <>
                <FormAccessSettings
                    embedded
                    ref={formAccessRef}
                    formId={formId}
                    formName={name}
                />
            </>
        )
},
        {
key: 'editHistory',
label: 'Edit History',
readOnly: false,
show: !!formId && !!responseEditing?.allowResponseEditing,
node: (
            <>
                {!!formId && !!responseEditing?.allowResponseEditing && (
                    <SettingsSectionBase
                        header="Edit History"
                        headerIcon={<HistoryOutlinedIcon fontSize="small"/>}
                    >
                        <FormEditHistory formId={formId}/>
                    </SettingsSectionBase>
                )}
            </>
        )
},
        {
key: 'approvals',
label: 'Approvals',
readOnly: isFormArchived,
show: !!shouldShowApprovalSettings,
node: (
            <>
                {shouldShowApprovalSettings && (
                    <ApprovalSettings
                        embedded
                        formId={formId}
                        schema={schema}
                        isFormLive={isFormLive}
                        isFormPrivate={accessLevel === FORM_ACCESS_LEVELS.private}
                        isFormArchived={isFormArchived}
                    />
                )}
            </>
        )
},
        {
key: 'autoresponses',
label: 'Automatic Responses',
readOnly: isFormArchived,
show: !isCommunicationMode,
node: (
            <>
                {!isCommunicationMode && (
                    <AutoresponderSettings
                        embedded
                        formId={formId}
                        schema={schema}
                        enableAutoresponder={enableAutoresponder}
                        onAutoresponderToggle={onAutoresponderToggle}
                        accessLevel={accessLevel}
                        isFormArchived={isFormArchived}
                        audience={audience}
                        recipientType={recipientType}
                        linkedToStudents={linkToStudents}
                        linkedToEmployees={linkToEmployees}
                    />
                )}
            </>
        )
},
    ].filter(pane => pane.show);
    const shownSettingsSection = settingsPanes.some(pane => pane.key === activeSettingsSection)
        ? activeSettingsSection
        : (settingsPanes.find(pane => pane.key === 'recipients') || settingsPanes.find(pane => !pane.readOnly))?.key;

    return (
        <Box sx={{
            py: 2,
            px: {
                xs: 2,
                md: SETTINGS_SIDE_CLEARANCE,
            }
        }}>
            <Box sx={{display: 'flex', gap: 3, alignItems: 'flex-start', width: '100%', maxWidth: '1800px', mx: 'auto'}}>
                <Box
                    component="nav"
                    data-testid="form-settings-sidebar"
                    sx={{
                        width: 210,
                        flexShrink: 0,
                        position: 'sticky',
                        // AD-2705: below the logo bar and the editor's tab bar, which now stay in view too.
                        top: 'calc(var(--app-header-height, 0px) + var(--sticky-bar-height, 0px) + 16px)',
                        backgroundColor: '#fff',
                        border: '1px solid #e0e0e0',
                        borderRadius: 2,
                        py: 0.5,
                    }}
                >
                    {settingsPanes.map(pane => {
                        const selected = pane.key === shownSettingsSection;
                        return (
                            <Box
                                key={pane.key}
                                component="button"
                                data-testid={`form-settings-nav-${pane.key}`}
                                onClick={() => setActiveSettingsSection(pane.key)}
                                sx={{
                                    display: 'block',
                                    width: '100%',
                                    textAlign: 'left',
                                    px: 2,
                                    py: 0.75,
                                    border: 0,
                                    borderLeft: selected ? '3px solid #186BBE' : '3px solid transparent',
                                    backgroundColor: selected ? '#f2f7fd' : 'transparent',
                                    color: selected ? '#186BBE' : (pane.readOnly ? 'text.secondary' : 'text.primary'),
                                    fontWeight: selected ? 600 : 400,
                                    fontSize: 14,
                                    fontFamily: 'inherit',
                                    cursor: 'pointer',
                                    '&:hover': {backgroundColor: '#f5f7fa'},
                                }}
                            >
                                {pane.label}
                            </Box>
                        );
                    })}
                </Box>
                <Box sx={{flex: 1, minWidth: 0}}>
                    {settingsPanes.map(pane => (
                        <Box
                            key={pane.key}
                            data-settings-section={pane.key}
                            sx={{display: pane.key === shownSettingsSection ? 'flex' : 'none', flexDirection: 'column', gap: 3}}
                        >
                            {pane.node}
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

PrivateFormSettings.defaultProps = {
    isFormLive: false,
};

PrivateFormSettings.propTypes = {
    requestedSection: PropTypes.shape({section: PropTypes.string, at: PropTypes.number}),
    templateName: PropTypes.string,
    template: PropTypes.object,
    formId: PropTypes.number,
    name: PropTypes.string,
    onNameChange: PropTypes.func,
    expirationDate: PropTypes.string,
    remindInterval: PropTypes.number,
    onRemindIntervalChange: PropTypes.func,
    onExpirationDateChange: PropTypes.func,
    categories: PropTypes.array,
    onCategoriesChange: PropTypes.func,
    formCategories: PropTypes.array,
    isFormLive: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    strictRecipientsFiltering: PropTypes.bool,
    audience: PropTypes.number,
    parentAudienceType: PropTypes.number,
    audienceOptions: PropTypes.array,
    settings: PropTypes.object,
    originalRecipientSettings: PropTypes.object,
    externalUsers: PropTypes.array,
    allParentsCanSubmit: PropTypes.bool,
    sendToPriority1ContactsOnly: PropTypes.bool,
    onRollOnly: PropTypes.bool,
    visibleToParents: PropTypes.bool,
    shareResponseWithOtherParents: PropTypes.bool,
    showResponsesInLinkedProfile: PropTypes.bool,
    onShowResponsesInLinkedProfileChange: PropTypes.func,
    showResponsesToLinkedParents: PropTypes.bool,
    responseEditing: PropTypes.shape({
        allowResponseEditing: PropTypes.bool,
        editUpdatesProfile: PropTypes.bool,
        editResendsResponses: PropTypes.bool,
    }),
    onResponseEditingChange: PropTypes.func,
    onShowResponsesToLinkedParentsChange: PropTypes.func,
    profileMustBeComplete: PropTypes.bool,
    onProfileMustBeComplete: PropTypes.func,
    allowMultipleSubmissions: PropTypes.bool,
    accessLevel: PropTypes.number,
    notificationEmails: PropTypes.array,
    onSettingsChange: PropTypes.func,
    onGroupFiltersChange: PropTypes.func,
    onStudentFiltersChange: PropTypes.func,
    onSetStrictRecipientsFiltering: PropTypes.func,
    onAudienceChange: PropTypes.func,
    onParentAudienceTypeChange: PropTypes.func,
    onAllParentsCanSubmitChange: PropTypes.func,
    onSendToPriority1ContactsOnlyChange: PropTypes.func,
    onVisibleToParentsChange: PropTypes.func,
    onShareResponseWithOtherParentsChange: PropTypes.func,
    onAllowMultipleSubmissionsChange: PropTypes.func,
    onAccessLevelChange: PropTypes.func,
    onNotificationEmailsChange: PropTypes.func,
    enableRecipientNotifications: PropTypes.bool,
    onEnableRecipientNotificationsChange: PropTypes.func,
    isCommunicationForm: PropTypes.bool,
    onCommunicationTypeChange: PropTypes.func,
    canSwitchToEmail: PropTypes.bool,
    enableTrigger: PropTypes.bool,
    setEnableTrigger: PropTypes.func,
    formQuestionAssigment: PropTypes.object,
    linkToStudents: PropTypes.bool,
    onLinkToStudentsChange: PropTypes.func,
    linkToEmployees: PropTypes.bool,
    onLinkToEmployeesChange: PropTypes.func,
    hasInitialisedRecipients: PropTypes.bool,
    allLinkedEmployeesCanSubmit: PropTypes.bool,
    onAllLinkedEmployeesCanSubmitChange: PropTypes.func,
    formAccessRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({current: PropTypes.any})
    ]),
    schema: PropTypes.array,
    enableAutoresponder: PropTypes.bool,
    onAutoresponderToggle: PropTypes.func,
    recipientType: PropTypes.string,
};

export default PrivateFormSettings;
